import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { FileDown } from "lucide-react";
import { sharedExportarPdf } from "../utils/shared-functions";

export default function DetalleProductoModal({ isOpen, onClose, data = [] }) {
    const { t } = useTranslation();
    const modalRef = useRef(null);

    const contentRef = useRef(null);

    if (!isOpen) return null;

    // GROUP DATA BY CLIENT
    const agruparPorCliente = (data = []) => {
        // console.log('agruparPorCliente', data);
        const grouped = data.reduce((acc, item) => {
            if (!acc[item.idCliente]) {
                acc[item.idCliente] = {
                    nombreCliente: item.nombreCliente,
                    productos: [],
                    total: 0
                };
            }

            acc[item.idCliente].productos.push(item);
            acc[item.idCliente].total += Number(item.monto);

            return acc;
        }, {});
        // console.log('grouped', grouped);
        return Object.values(grouped);
    };

    const clientesAgrupados = agruparPorCliente(data);

    const totalGeneral = data.reduce((sum, item) => {
        return sum + Number(item.monto);
    }, 0).toFixed(2);

    const handleOutsideClick = (e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            onMouseDown={handleOutsideClick}
        >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/10" />

            {/* Modal */}
            <div
                ref={modalRef}
                className="relative bg-white rounded-lg shadow-lg w-[calc(100%-1.5rem)] max-w-[8.5in] sm:w-[calc(100%-2.5rem)] max-h-[90vh] flex flex-col"
            >
                {/* Header */}
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-lg font-semibold">
                        {t("label_detalle_entrega")}
                    </h2>
                    <button
                        type="button"
                        onClick={() => sharedExportarPdf(contentRef)}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium"
                    >
                        <FileDown className="h-4 w-4" />
                        {t("label_exportar_pdf")}
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="bg-white text-black p-6 overflow-y-auto"
                    ref={contentRef}
                    style={{
                        fontFamily: "sans-serif",
                    }}
                >

                    {clientesAgrupados.length === 0 ? (
                        <p className="text-gray-500 text-sm" data-pdf-block>
                            {t("label_no_hay_informacion_disponible")}
                        </p>
                    ) : (
                        clientesAgrupados.map((cliente, index) => {
                            // console.log('cliente', cliente);
                            return (
                                <div
                                    key={index}
                                    data-pdf-block
                                    className="mb-4 border border-gray-200 rounded-lg overflow-hidden"
                                >
                                    <table className="w-full max-w-full text-sm border-collapse table-fixed">
                                        <colgroup>
                                            <col style={{ width: "58%" }} />
                                            <col style={{ width: "22%" }} />
                                            <col style={{ width: "20%" }} />
                                        </colgroup>
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-200">
                                                <th
                                                    colSpan={3}
                                                    scope="colgroup"
                                                    className="text-left font-semibold text-gray-800 py-2 px-3 align-bottom"
                                                >
                                                    {index + 1}. {cliente.nombreCliente}
                                                </th>
                                            </tr>
                                            <tr className="bg-gray-50 text-xs font-semibold text-gray-600 border-b border-gray-200">
                                                <th scope="col" className="text-left py-2 px-3 font-semibold">
                                                    {t("label_productos")}
                                                </th>
                                                <th scope="col" className="text-right py-2 px-3 font-semibold">
                                                    {t("label_cantidad")}
                                                </th>
                                                <th scope="col" className="text-right py-2 px-3 font-semibold">
                                                    {t("label_monto")}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cliente.productos.map((producto, idx) => (
                                                <tr
                                                    key={idx}
                                                    className="border-b border-gray-100"
                                                >
                                                    <td className="py-2 px-3 align-top text-gray-900 break-words leading-normal">
                                                        {idx + 1}. {producto.nombreProducto}
                                                    </td>
                                                    <td className="py-2 px-3 align-top text-right tabular-nums text-gray-800 whitespace-nowrap leading-normal">
                                                        {producto.cantidad} {producto.unidadMedida}
                                                    </td>
                                                    <td className="py-2 px-3 align-top text-right tabular-nums font-medium text-gray-900 whitespace-nowrap leading-normal">
                                                        {producto.monto}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr
                                                className="bg-gray-50 border-t border-gray-200 font-semibold text-gray-900"
                                            >
                                                <td colSpan={2} className="py-2 px-3 text-left">
                                                    Total Cliente
                                                </td>
                                                <td className="py-2 px-3 text-right tabular-nums">
                                                    {cliente.total.toFixed(2)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            );
                        })
                    )}

                    {clientesAgrupados.length > 0 && (
                        <table
                            className="w-full max-w-full text-sm border-collapse table-fixed border border-gray-200 rounded-lg overflow-hidden mt-4"
                            data-pdf-block
                        >
                            <colgroup>
                                <col style={{ width: "80%" }} />
                                <col style={{ width: "20%" }} />
                            </colgroup>
                            <tbody>
                                <tr className="border-t-2 border-gray-300 font-semibold text-gray-900 bg-gray-50">
                                    <td className="py-3 px-3">Total General</td>
                                    <td className="py-3 px-3 text-right tabular-nums">
                                        {totalGeneral}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}

                </div>

                {/* Footer */}
                <div className="p-6 border-t flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
                    >
                        {t("label_cerrar")}
                    </button>
                </div>

            </div>
        </div>
    );
}